import type { ApiResponse } from "../types";

let csrfToken: string | null = null;
let authToken: string | null = typeof localStorage !== "undefined" ? localStorage.getItem("zimsmart_token") : null;

async function parse<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get("content-type") || "";
  let payload: ApiResponse<T> | null = null;

  if (contentType.includes("application/json")) {
    try {
      payload = (await response.json()) as ApiResponse<T>;
    } catch {
      throw new Error(`Server returned invalid JSON (HTTP ${response.status})`);
    }
  } else {
    const text = await response.text();
    try {
      payload = JSON.parse(text) as ApiResponse<T>;
    } catch {
      if (!response.ok) {
        throw new Error(`Server error (${response.status}): ${response.statusText || "Request failed"}`);
      }
      throw new Error(`Unexpected server response format (HTTP ${response.status})`);
    }
  }

  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.message || `Request failed with status ${response.status}`);
  }
  return payload.data;
}

export async function getCsrf() {
  if (csrfToken) return csrfToken;
  try {
    const data = await apiGet<{ csrfToken: string }>("/api/csrf");
    csrfToken = data.csrfToken;
    return csrfToken;
  } catch {
    return "";
  }
}

export function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers["x-csrf-token"] = csrfToken;
  }
  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`;
  }
  return headers;
}

export async function apiGet<T>(url: string): Promise<T> {
  const headers = getAuthHeaders();
  const response = await fetch(url, {
    credentials: "include",
    headers
  });
  return parse<T>(response);
}

async function writeRequest<T>(url: string, options: RequestInit, isUpload: boolean): Promise<T> {
  for (let attempt = 0; attempt < 2; attempt++) {
    const token = await getCsrf();
    const response = await fetch(url, {
      ...options,
      credentials: "include",
      headers: {
        ...(!isUpload ? { "Content-Type": "application/json" } : {}),
        ...(token ? { "x-csrf-token": token } : {}),
        ...getAuthHeaders(),
        ...(options.headers ?? {})
      }
    });

    if (response.status === 403 && attempt === 0) {
      const body = await response.clone().json().catch(() => null);
      if (body?.code === "CSRF_INVALID" || body?.error?.code === "CSRF_INVALID") {
        csrfToken = null;
        continue;
      }
    }
    return parse<T>(response);
  }
  throw new Error("Request failed after CSRF refresh");
}

export async function apiSend<T>(url: string, options: RequestInit = {}): Promise<T> {
  return writeRequest<T>(url, options, false);
}

export async function apiUpload<T>(url: string, formData: FormData): Promise<T> {
  return writeRequest<T>(url, { method: "POST", body: formData }, true);
}

export function resetCsrf(token?: string | null) {
  csrfToken = token ?? null;
}

export function setAuthToken(token?: string | null) {
  authToken = token ?? null;
  if (token) {
    localStorage.setItem("zimsmart_token", token);
  } else {
    localStorage.removeItem("zimsmart_token");
  }
}

export function getAuthToken() {
  return authToken;
}
